import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useNavigate } from 'react-router-dom';
import { vehicleService } from '../../../application/VehicleService';
import * as routing from '../../../constants/Routing';
import { Vehicle } from '../../../domain/vehicle/Vehicle';
import { useRetrieveActiveLanguage } from '../../../hooks/translations/useRetrieveActiveLanguage';
import { useErrorMiddleware } from '../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../hooks/useShowSnackbarMessage';

interface UseUpdateVehicleLocationProps {
  location: { lat: number; lng: number };
}

type UseUpdateVehicleLocationResponseProps = ServiceResponse<
  ({ location }: UseUpdateVehicleLocationProps) => void,
  Vehicle
>;

// Hooks act as 'Adapter' layer.
export const useUpdateVehicleLocation = (): UseUpdateVehicleLocationResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const navigate = useNavigate();
  const { activeLanguage } = useRetrieveActiveLanguage();
  const queryClient = useQueryClient();

  const { isLoading, error, isSuccess, mutate } = useMutation(
    ({ location }: UseUpdateVehicleLocationProps) => {
      return vehicleService.updateRegisterLocation({
        ...location,
        lang: activeLanguage,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([`drivers_vehicle`]);
      },
      onError: (err: any) => {
        console.info(`Update Vehicle Location Error: `, err?.response);

        if (err?.response?.data?.messageId === `highway.subscription.error.expired_subscription`) {
          navigate(routing.SUBSCRIPTION_EXPIRED);
        } else {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error,
    success: isSuccess,
    isLoading: isLoading,
    run: mutate,
  };
};
